import React, { useState, useMemo, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

// ------------------ Helpers ------------------
const safeParseNumber = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") {
    return Number.isFinite(v) ? v : 0;
  }
  const cleaned = String(v).replace(/,/g, "").trim();
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const roundToTwoDecimalPlaces = (num) => {
  if (!Number.isFinite(num)) return 0;
  return Math.round(num * 100) / 100;
};

// Calculation helpers (defensive)
const calculateqtychanges = (data) => {
  const currentRev = safeParseNumber(data.currentRev);
  const currentRate = safeParseNumber(data.currentRate);
  if (currentRate === 0) return 0;
  const currentqty = currentRev / currentRate;
  return roundToTwoDecimalPlaces(currentqty);
};

const calculateIncrementRev = (data) => {
  const currentQty = safeParseNumber(data.currentQty ?? calculateqtychanges(data));
  const currentRate = safeParseNumber(data.currentRate);
  const quantityImpact = safeParseNumber(data.quantityImpact);
  const rateImpact = safeParseNumber(data.rateImpact);

  if (currentQty === 0 || currentRate === 0) return 0;
  if (quantityImpact === 0 && rateImpact === 0) return 0;

  const newRate = currentRate * (1 - rateImpact / 100);
  const qtyIncrement = (currentQty * quantityImpact) / 100;
  const adjustedQty = currentQty + qtyIncrement;
  const impactedAmount = newRate * adjustedQty;
  const baseAmount = currentRate * currentQty;
  const difference = impactedAmount - baseAmount;
  return roundToTwoDecimalPlaces(difference);
};

const calculateRevGrowth = (data) => {
  const currentRev = safeParseNumber(data.currentRev);
  if (currentRev === 0) return 0;
  const incrementRev = calculateIncrementRev(data);
  if (incrementRev === 0) return 0;
  return roundToTwoDecimalPlaces((incrementRev / currentRev) * 100);
};

const calculateDiscountSpend = (data) => {
  const currentQty = safeParseNumber(data.currentQty ?? calculateqtychanges(data));
  const currentRate = safeParseNumber(data.currentRate);
  const rateImpact = safeParseNumber(data.rateImpact);
  const quantityImpact = safeParseNumber(data.quantityImpact);

  if (currentQty === 0 || currentRate === 0 || rateImpact==0 || quantityImpact==0) return 0;

  const adjustedRate = currentRate * (1 - rateImpact / 100);
  const qtyIncrement = (currentQty * quantityImpact) / 100;
  const totalQty = currentQty + qtyIncrement;
  const finalValue = adjustedRate * totalQty;
  return roundToTwoDecimalPlaces(finalValue);
};

const calculateTotalRev = (data) => {
  const currentRev = safeParseNumber(data.currentRev);
  const incrementRev = safeParseNumber(calculateIncrementRev(data));
  return roundToTwoDecimalPlaces(currentRev + incrementRev);
};

// ------------------ Component ------------------
/**
 * Props:
 * - warehouseData: array
 * - onWarehouseDetailsChange(updatedRows)
 * - onDeleteScheme(row)
 * - modalFormData: object { schemeType: "Correction" | ..., highlightCorrectionFields?: boolean, ... }
 * - onModalFormDataChange(updatedModalFormData)  (optional) 
 *
 * onModalFormDataChange will be called with an object that includes:
 *   { ...modalFormData, correctionFlag: boolean, warehouseData: updatedRows }
 */
const SchemeDetails = ({
  warehouseData = [],
  onWarehouseDetailsChange,
  onDeleteScheme,
  modalFormData = {},
  onModalFormDataChange
}) => {
  const [rowData, setRowData] = useState([]);

  // Keep original Total Rev map to detect revert (by row id)
  const [originalTotalRevMap, setOriginalTotalRevMap] = useState({});

  // Initialize rowData with numeric values, stable ids, and correctionFlag = false
  useEffect(() => {
    const mapped = (warehouseData || []).map((w, idx) => {
      const currentRate = safeParseNumber(w.currentRate);
      const currentRev = safeParseNumber(w.currentRev);
      const rateImpact = safeParseNumber(w.rateImpact);
      const quantityImpact = safeParseNumber(w.quantityImpact);

      const totalRevFromSource = safeParseNumber(w["Total Rev"] ?? w.totalRev);
      const computedIncrement = calculateIncrementRev({
        ...w,
        currentRate,
        currentRev,
        rateImpact,
        quantityImpact,
      });
      const computedTotalRev = roundToTwoDecimalPlaces(currentRev + computedIncrement);

      const id = w.id ?? w.Warehouse ?? `__rid_${idx}`;
      return {
        ...w,
        id,
        currentRate,
        currentRev,
        rateImpact,
        quantityImpact,
        // keep the 'Total Rev' key as your UI expects
        ["Total Rev"]: totalRevFromSource || computedTotalRev,
        // per-row correction flag (preserve if parent provided)
        correctionFlag: !!w.correctionFlag
      };
    });

    // Build original map
    const map = {};
    for (const r of mapped) {
      map[r.id] = safeParseNumber(r["Total Rev"]);
    }

    setOriginalTotalRevMap(map);
    setRowData(mapped);
  }, [warehouseData]);

  // ------------------ Renderers ------------------
  const DeleteButtonRenderer = (props) => {
    if (props.data && props.data.scheme_present === 1) {
      return (
        <Tooltip title="Delete Scheme">
          <IconButton
            size="small"
            onClick={() => onDeleteScheme && onDeleteScheme(props.data)}
            sx={{ color: "#d32f2f" }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    }
    return null;
  };

  const WarehouseCellRenderer = (props) => {
    const isSchemePresent = props.data && props.data.scheme_present === 1;
    return (
      <div
        style={{
          backgroundColor: isSchemePresent ? "#808080" : "transparent",
          fontWeight: isSchemePresent ? "bold" : "normal",
          padding: "8px",
          borderRadius: "4px",
          border: isSchemePresent ? "1px solid #ff9800" : "none",
        }}
      >
        {props.value}
      </div>
    );
  };

  // ------------------ Column Definitions ------------------
  const columnDefs = useMemo(() => [
    {
      headerName: "Warehouse",
      field: "Warehouse",
      cellRenderer: WarehouseCellRenderer,
      width: 120,
      editable: false
    },
    { headerName: "Current MRP", field: "MRP", hide: true },
    {
      headerName: "Current Rate",
      field: "currentRate",
      cellRenderer: WarehouseCellRenderer,
      width: 150,
      editable: () => modalFormData.schemeType === "Correction",
      valueFormatter: (params) => roundToTwoDecimalPlaces(safeParseNumber(params.value)),
      cellStyle: () => (
        modalFormData.schemeType === "Correction" && modalFormData.highlightCorrectionFields
          ? { backgroundColor: "#fff3cd" }
          : {}
      )
    },
    {
      headerName: "Current Rev",
      field: "currentRev",
      editable: () => modalFormData.schemeType === "Correction",
      cellRenderer: WarehouseCellRenderer,
      width: 150,
      valueFormatter: (params) => roundToTwoDecimalPlaces(safeParseNumber(params.value)),
      cellStyle: () => (
        modalFormData.schemeType === "Correction" && modalFormData.highlightCorrectionFields
          ? { backgroundColor: "#fff3cd" }
          : {}
      )
    },
    {
      headerName: "Current Qty",
      field: "currentQty",
      valueGetter: (params) => roundToTwoDecimalPlaces(calculateqtychanges(params.data)),
      cellRenderer: WarehouseCellRenderer,
      width: 150,
      editable: false
    },
    {
      headerName: "Rate Impact (%)",
      field: "rateImpact",
      editable: () => modalFormData.schemeType !== "Correction",
      cellRenderer: WarehouseCellRenderer,
      width: 150,
      valueFormatter: (params) => roundToTwoDecimalPlaces(safeParseNumber(params.value))
    },
    {
      headerName: "Qty Impact (%)",
      field: "quantityImpact",
      editable: () => modalFormData.schemeType !== "Correction",
      cellRenderer: WarehouseCellRenderer,
      width: 150,
      valueFormatter: (params) => roundToTwoDecimalPlaces(safeParseNumber(params.value))
    },
    {
      headerName: "Qty Increment",
      field: "qtyIncrement",
      editable: false,
      valueGetter: (params) => {
        const currentQty = safeParseNumber(params.data.currentQty ?? calculateqtychanges(params.data));
        const quantityImpact = safeParseNumber(params.data.quantityImpact);
        return roundToTwoDecimalPlaces((currentQty * quantityImpact) / 100);
      },
      cellRenderer: WarehouseCellRenderer,
      width: 150
    },
    {
      headerName: "Discount Spend",
      field: "discountSpend",
      editable: false,
      valueGetter: (params) => calculateDiscountSpend(params.data),
      cellRenderer: WarehouseCellRenderer,
      width: 150
    },
    {
      headerName: "Increment Rev",
      field: "Increment Rev",
      editable: false,
      valueGetter: (params) => {
        if (modalFormData.schemeType === 'Correction') {
          const total = safeParseNumber(params.data["Total Rev"]);
          const cur = safeParseNumber(params.data.currentRev);
          return roundToTwoDecimalPlaces(total - cur);
        }
        return calculateIncrementRev(params.data);
      },
      cellRenderer: WarehouseCellRenderer,
      width: 150
    },
    {
      headerName: "Rev Growth (%)",
      field: "Rev Growth (%)",
      editable: false,
      valueGetter: (params) => {
        if (modalFormData.schemeType === 'Correction') {
          const total = safeParseNumber(params.data["Total Rev"]);
          const cur = safeParseNumber(params.data.currentRev);
          if (cur === 0) return 0;
          const incrementRev = total - cur;
          return roundToTwoDecimalPlaces((incrementRev / cur) * 100);
        }
        return calculateRevGrowth(params.data);
      },
      cellRenderer: WarehouseCellRenderer,
      width: 150
    },
    {
      headerName: "Total Rev",
      field: "Total Rev",
      editable: () => modalFormData.schemeType === "Correction",
      valueGetter: (params) => {
        if (modalFormData.schemeType === "Correction") {
          return safeParseNumber(params.data["Total Rev"]);
        }
        return calculateTotalRev(params.data);
      },
      cellRenderer: WarehouseCellRenderer,
      width: 150,
      cellStyle: () => (
        modalFormData.schemeType === "Correction" && modalFormData.highlightCorrectionFields
          ? { backgroundColor: "#fff3cd" }
          : {}
      )
    },
    // Per-row corrected indicator
    {
      headerName: "Corrected",
      field: "correctionFlag",
      width: 100,
      editable: false,
      hide:true,
      valueGetter: (params) => (params.data && params.data.correctionFlag ? "Yes" : ""),
      cellStyle: (params) => (
        params.data && params.data.correctionFlag ? { backgroundColor: "#e8f5e9", fontWeight: "bold" } : {}
      )
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: DeleteButtonRenderer,
      width: 80,
      sortable: false,
      filter: false,
      editable: false
    }
  ], [modalFormData]);

  // ------------------ Cell change handler ------------------
  const numericLikeFields = new Set([
    "currentRate",
    "currentRev",
    "rateImpact",
    "quantityImpact",
    "Total Rev",
    "MRP",
    "Qty Increment",
    "qtyIncrement",
    "Increment Rev",
    "discountSpend"
  ]);

  const onCellValueChanged = (params) => {
    const { data, colDef, newValue } = params;
    const updatedData = [...rowData];
    const rowIndex = updatedData.findIndex(row => row.id === data.id);
    if (rowIndex === -1) return;

    const newRow = { ...data };
    const field = colDef.field;

    // parse numeric-like fields safely
    if (numericLikeFields.has(field)) {
      newRow[field] = safeParseNumber(newValue);
    } else {
      newRow[field] = newValue;
    }

    // If Total Rev edited, detect change vs original and set/unset per-row correctionFlag
    if (field === "Total Rev") {
      const prevTotal = originalTotalRevMap[data.id] ?? safeParseNumber(data["Total Rev"]);
      const newTotal = safeParseNumber(newRow["Total Rev"]);

      if (newTotal !== prevTotal) {
        newRow.correctionFlag = true;
      } else {
        newRow.correctionFlag = false;
      }

      // Also recalc currentRev in Correction mode (reverse calculation)
      if (modalFormData.schemeType === "Correction") {
        const rateImpact = safeParseNumber(newRow.rateImpact);
        const quantityImpact = safeParseNumber(newRow.quantityImpact);

        const rateFactor = 1 - rateImpact / 100;
        const qtyFactor = 1 + quantityImpact / 100;
        const combinedFactor = rateFactor * qtyFactor - 1;

        const total = safeParseNumber(newRow['Total Rev']);
        const denom = 1 + combinedFactor;
        if (!Number.isFinite(denom) || Math.abs(denom) < 1e-9) {
          newRow.currentRev = roundToTwoDecimalPlaces(total);
        } else {
          newRow.currentRev = roundToTwoDecimalPlaces(total / denom);
        }
      }
    }

    // update local state
    updatedData[rowIndex] = newRow;
    setRowData(updatedData);

    // notify parent with updated rows (backwards compatible)
    if (typeof onWarehouseDetailsChange === "function") {
      onWarehouseDetailsChange(updatedData);
    }

    // compute parent-level correctionFlag = true if any row has correctionFlag true
    const parentCorrectionFlag = updatedData.some(r => !!r.correctionFlag);

    // send modalFormData with correctionFlag and updated warehouseData
    if (typeof onModalFormDataChange === "function") {
      onModalFormDataChange({
        ...(modalFormData || {}),
        correctionFlag: parentCorrectionFlag,
        warehouseData: updatedData
      });
    }
  };

  // ------------------ Render ------------------
  return (
    <div className="ag-theme-alpine" style={{ height: 400, width: "100%" }}>
      <AgGridReact
        rowData={rowData}
        getRowId={(params) => params.data.id}
        columnDefs={columnDefs}
        defaultColDef={{
          resizable: true,
          sortable: true,
          editable: true,
        }}
        stopEditingWhenCellsLoseFocus
        onCellValueChanged={onCellValueChanged}
      />
    </div>
  );
};

export default SchemeDetails;
